import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { getViewSettings } from '@adornis/buchungsheld-db/api/AdminInstitutionViewSetting.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, filter, map, of, switchMap } from 'rxjs';

/**
 * @element bh-timeframe-selection
 */
@customElement('bh-timeframe-selection')
export class BhTimeframeSelection extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @state() private readonly _timeframes = new RXController(this, this.institutionID.observable, institutionID$ =>
    institutionID$.pipe(
      switchMap(institutionID =>
        institutionID ? Timeframe.getTimeframesByInstitution(institutionID)(Timeframe.allFields) : of([]),
      ),
    ),
  );

  @state() private readonly _selectedTimeframe = new RXController(
    this,
    combineLatest([this.institutionID.observable, currentUser()]).pipe(
      filter(([institutionID, currentUser]) => !!institutionID && !!currentUser),
      switchMap(([institutionID, currentUser]) =>
        getViewSettings(currentUser._id, institutionID)({ selectedTimeframeID: 1 }),
      ),
      map(viewSettings => viewSettings?.selectedTimeframeID),
      filter(isDefined),
      switchMap(timeframeID => (timeframeID ? getByID(Timeframe, timeframeID)(Timeframe.allFields) : of(null))),
    ),
  );

  override render() {
    return html`
      <x-dropdown-selection
        .emptyState=${this._translation.translate('dropdowns', 'emptyStateTimeframes')}
        ${css({ display: 'block', minWidth: '150px' })}
        .selectables=${['all'].concat(this._timeframes.value ?? [])}
        .value=${this._selectedTimeframe.value}
        @value-picked=${(e: ValueEvent<Timeframe | 'all' | null>) => {
          const institutionID = this.institutionID.value;
          if (!institutionID) return;
          const timeframeID = (() => {
            if (!e.detail.value) return null;
            if (e.detail.value === 'all') return null;
            return e.detail.value._id;
          })();

          const action = (async () => {
            const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
            if (!currentUser) throw new Error('No current user found');
            const viewSettings = await getViewSettings(currentUser._id, institutionID)({ selectedTimeframeID: 1 });
            viewSettings!.selectedTimeframeID = timeframeID;
            await viewSettings!.save();
          })();

          void XDialog.waitFor(action);
        }}
        placeholder-mode="hide"
        placeholder="Alle Semester"
        .renderString=${(timeframe?: Timeframe | 'all') => (timeframe === 'all' ? 'Alle Semester' : timeframe?.name)}
      ></x-dropdown-selection>
    `;
  }
}
